import React, { useState, useEffect, useCallback } from "react";
import { ZoomIn, ZoomOut, RotateCcw, RotateCw, X, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";

const SalesGallery = ({ image, imgIndex, title, images = [] }) => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(imgIndex || 0);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);

  // Allowed image extensions
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];

  // Check if the file is an image
  const isImage = (filename) => {
    if (!filename) return false;
    const ext = filename.split(".").pop().toLowerCase();
    return imageExtensions.includes(ext);
  };

  const openModal = () => {
    if (isImage(image)) {
      setCurrentIndex(imgIndex || 0);
      setSelectedImage(process.env.REACT_APP_API_URL_IMAGE + image);
      setScale(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
      setLoading(true);
      
      // Push state to browser history to handle mobile back button closing the modal
      window.history.pushState({ modalOpen: true }, "");
    } else {
      alert(t("file_not_image") || "This file is not an image.");
    }
  };

  const closeModal = () => {
    setSelectedImage(null);
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    setLoading(true);

    // If modal state exists in history, go back to pop it
    if (window.history.state?.modalOpen) {
      window.history.back();
    }
  };

  const showNext = useCallback(() => {
    if (images && images.length > 1) {
      const nextIndex = (currentIndex + 1) % images.length;
      setCurrentIndex(nextIndex);
      setSelectedImage(process.env.REACT_APP_API_URL_IMAGE + images[nextIndex]);
      setScale(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
      setLoading(true);
    }
  }, [currentIndex, images]);

  const showPrev = useCallback(() => {
    if (images && images.length > 1) {
      const prevIndex = (currentIndex - 1 + images.length) % images.length;
      setCurrentIndex(prevIndex);
      setSelectedImage(process.env.REACT_APP_API_URL_IMAGE + images[prevIndex]);
      setScale(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
      setLoading(true);
    }
  }, [currentIndex, images]);

  // Rotate Handler - تەنها پلەی خولانەوە دەگۆڕێت بەبێ دەستکاری کردنی Zoom و Position
  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  // Drag handlers for mouse
  const handleMouseDown = (e) => {
    if (scale === 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile (swipe & pan)
  const handleTouchStart = (e) => {
    if (scale === 1) {
      const touch = e.touches[0];
      setDragStart({ x: touch.clientX, y: touch.clientY });
      return;
    }
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
  };

  const handleTouchMove = (e) => {
    if (scale === 1) return;
    if (!isDragging) return;
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };

  const handleTouchEnd = (e) => {
    if (scale === 1) {
      if (e.changedTouches && e.changedTouches.length > 0) {
        const touch = e.changedTouches[0];
        const diffX = touch.clientX - dragStart.x;
        const diffY = touch.clientY - dragStart.y;
        if (Math.abs(diffX) > 50 && Math.abs(diffY) < 100) {
          if (diffX > 0) {
            showPrev();
          } else {
            showNext();
          }
        }
      }
      return;
    }
    setIsDragging(false);
  };

  // Mouse wheel zoom
  const handleWheel = (e) => {
    if (!selectedImage || loading) return;
    e.preventDefault();
    const zoomIntensity = 0.05;
    setScale((s) => {
      let nextScale = s - e.deltaY * zoomIntensity * 0.05;
      nextScale = Math.min(Math.max(nextScale, 1), 4);
      if (nextScale === 1) setPosition({ x: 0, y: 0 });
      return nextScale;
    });
  };

  // Double click / Double tap to zoom
  const handleDoubleClick = () => {
    if (scale > 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(2.5);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeModal();
      } else if (e.key === "+" || e.key === "=") {
        setScale((s) => Math.min(s + 0.25, 4));
      } else if (e.key === "-" || e.key === "_") {
        setScale((s) => {
          const next = Math.max(s - 0.25, 1);
          if (next === 1) setPosition({ x: 0, y: 0 });
          return next;
        });
      } else if (scale > 1) {
        const step = 30;
        if (e.key === "ArrowUp") {
          setPosition((pos) => ({ ...pos, y: pos.y + step }));
        } else if (e.key === "ArrowDown") {
          setPosition((pos) => ({ ...pos, y: pos.y - step }));
        } else if (e.key === "ArrowLeft") {
          setPosition((pos) => ({ ...pos, x: pos.x + step }));
        } else if (e.key === "ArrowRight") {
          setPosition((pos) => ({ ...pos, x: pos.x - step }));
        }
      } else if (scale === 1 && images && images.length > 1) {
        if (e.key === "ArrowRight") {
          showNext();
        } else if (e.key === "ArrowLeft") {
          showPrev();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, scale, currentIndex, images, showNext, showPrev]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedImage]);

  // Handle back button navigation to close modal
  useEffect(() => {
    const handlePopState = (e) => {
      if (selectedImage) {
        setSelectedImage(null);
        setScale(1);
        setRotation(0);
        setPosition({ x: 0, y: 0 });
        setLoading(true);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [selectedImage]);

  if (!isImage(image)) return null;

  return (
    <div className="p-2 inline-block">
      {/* Image Grid Thumbnail */}
      <div className="justify-items-center">
        <img
          src={process.env.REACT_APP_API_URL_IMAGE + image}
          alt={title || "Sale Image"}
          className="w-24 h-24 sm:w-40 sm:h-40 object-cover z-10 rounded-2xl shadow-md border border-slate-200 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-indigo-400 bg-white"
          onClick={openModal}
        />
      </div>

      {/* Modal */}
      {selectedImage && createPortal(
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-slate-950/95 backdrop-blur-md z-[10000] transition-all duration-300 select-none">
          
          {/* Floating Header */}
          <div className="absolute top-0 left-0 right-0 p-4 sm:p-5 flex justify-between items-center bg-gradient-to-b from-black/60 to-transparent z-20">
            <span className="text-white text-base sm:text-lg font-bold tracking-wide truncate max-w-[80%] drop-shadow">
              {title || t("show_image") || "Image Viewer"}
              {images.length > 1 && ` (${currentIndex + 1} / ${images.length})`}
            </span>
            <button
              className="text-white hover:text-white bg-white/10 hover:bg-white/20 border border-white/10 rounded-full w-10 h-10 flex items-center justify-center transition duration-200 hover:scale-105 active:scale-95 shadow-lg"
              onClick={closeModal}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Left Arrow Button */}
          {images.length > 1 && (
            <button
              onClick={showPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-black/40 hover:bg-black/70 text-white rounded-full p-2.5 sm:p-3 transition-all duration-200 hover:scale-105 active:scale-95 border border-white/10"
              title={t("previous") || "Previous"}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Right Arrow Button */}
          {images.length > 1 && (
            <button
              onClick={showNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-black/40 hover:bg-black/70 text-white rounded-full p-2.5 sm:p-3 transition-all duration-200 hover:scale-105 active:scale-95 border border-white/10"
              title={t("next") || "Next"}
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Image Container */}
          <div 
            onWheel={handleWheel}
            className="relative w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing mt-1 select-none overflow-hidden"
          >
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-950/80 z-20">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 text-indigo-400 animate-spin" />
                  <span className="text-sm font-medium text-slate-300">{t("loading") || "Loading..."}</span>
                </div>
              </div>
            )}

            <img
              src={selectedImage}
              alt={title || "Selected View"}
              onLoad={() => setLoading(false)}
              className={`max-w-full max-h-[82vh] object-contain select-none transition-transform duration-100 ease-out ${loading ? "opacity-0" : "opacity-100"}`}
              style={{
                transform: `translate3d(${position.x}px, ${position.y}px, 0px) rotate(${rotation}deg) scale(${scale})`,
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onDoubleClick={handleDoubleClick}
              draggable={false}
            />

            {/* Floating Controls Bar */}
            {!loading && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-50">
                <div className="flex items-center gap-2 sm:gap-3 bg-black/70 backdrop-blur-md py-2 px-3 sm:px-4 rounded-2xl border border-white/10 shadow-2xl transition-all duration-200 text-white">
                  <button
                    onClick={() => {
                      setScale((s) => {
                        const next = Math.max(s - 0.25, 1);
                        if (next === 1) setPosition({ x: 0, y: 0 });
                        return next;
                      });
                    }}
                    disabled={scale === 1}
                    className="bg-white/10 hover:bg-white/20 disabled:opacity-20 disabled:cursor-not-allowed text-white w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl transition duration-150 active:scale-95"
                    title={t("zoom_out") || "Zoom Out"}
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  
                  <input
                    type="range"
                    min="1"
                    max="4"
                    step="0.05"
                    value={scale}
                    onChange={(e) => {
                      const nextScale = parseFloat(e.target.value);
                      setScale(nextScale);
                      if (nextScale === 1) setPosition({ x: 0, y: 0 });
                    }}
                    className="w-20 sm:w-36 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-indigo-500 focus:outline-none"
                  />
                  
                  <button
                    onClick={() => setScale((s) => Math.min(s + 0.25, 4))}
                    disabled={scale === 4}
                    className="bg-white/10 hover:bg-white/20 disabled:opacity-20 disabled:cursor-not-allowed text-white w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl transition duration-150 active:scale-95"
                    title={t("zoom_in") || "Zoom In"}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>

                  <button
                    onClick={handleRotate}
                    className="bg-white/10 hover:bg-white/20 text-white w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl transition duration-150 active:scale-95"
                    title={t("rotate") || "Rotate"}
                  >
                    <RotateCw className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => {
                      setScale(1);
                      setRotation(0);
                      setPosition({ x: 0, y: 0 });
                    }}
                    disabled={scale === 1 && rotation === 0 && position.x === 0 && position.y === 0}
                    className="bg-white/10 hover:bg-white/20 disabled:opacity-20 disabled:cursor-not-allowed text-white w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl transition duration-150 active:scale-95"
                    title={t("reset") || "Reset"}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>

                  <span className="text-xs font-bold text-indigo-300 bg-indigo-950/60 px-2 py-1 rounded-lg border border-indigo-500/20 select-none min-w-[45px] text-center">
                    {Math.round(scale * 100)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SalesGallery;